/* eslint-disable import/prefer-default-export */
import { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  isEmpty, find, forEach, filter
} from 'lodash';
import Big from 'big.js';

import useTimeoutTyping from '../../../../hooks/useTimeoutTyping';
import ProductModelMC from '../../../models/ProductModelMC';
import { canBeSelected, getProductTaxAmount, hasDiscount } from '../../../../util/productUtils';
import { uuid } from '../../../../util/utils';

export const useSearchProducts = ({
  selectedProducts,
  addProducts
}) => {
  const { retailer_info } = useSelector((reduxState) => reduxState.retailerUsersReducer);

  const { currency_code, currency_symbol, tax_amount } = retailer_info;

  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, has_next_page: false });
  const [selectedNewProducts, setSelectedNewProducts] = useState([]);
  const [searchedProductExists, setSearchedProductExists] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [searchText, setSearchText, isTyping] = useTimeoutTyping('', 500);

  useEffect(() => {
    if (!isTyping) searchProducts(1);
  }, [isTyping, searchText]);

  const addExtraAttributes = useCallback((resData) => {
    forEach(resData, (product) => {
      product.original_url = product.url;
      product.url = !isEmpty(product.image_url) ? product.image_url.url : '';
      product.selected = false;
      product.wasSelected = !!find(selectedProducts, { product_id: product.id });

      let selectedAllCombi = true;

      forEach(product.product_variant_combinations, (combination) => {
        combination.selected = false;
        combination.wasSelected = false;

        if (find(selectedProducts, { product_variant_combination_id: combination.id })) {
          combination.wasSelected = true;
        } else {
          selectedAllCombi = false;
        }
      });

      if (!isEmpty(product.product_variant_combinations)) product.wasSelected = selectedAllCombi;
    });

    return resData;
  }, [selectedProducts]);

  const searchProducts = useCallback((page = 1) => {
    if (isSearching) return;

    const productsQuery = selectedNewProducts
      .filter((el) => el._destroy !== true && !isNaN(el.product_id))
      .map((product) => `q[id_not_in][]=${product.product_id}`)
      .join('&');

    setIsSearching(true);

    ProductModelMC.getSelectProducts({ searchText, productsQuery, page }).then((res) => {
      setIsSearching(false);

      const resProducts = res?.products || (Array.isArray(res) ? res : []);
      const hasNextPage = res?.has_next_page || false;
      const currentPage = res?.current_page || page;

      setSearchedProductExists(!isEmpty(resProducts));

      const newProducts = addExtraAttributes(resProducts);

      if (page === 1) {
        setProducts(newProducts);
      } else {
        setProducts((prev) => [...(prev || []), ...newProducts]);
      }

      setPagination({
        current_page: currentPage,
        has_next_page: hasNextPage
      });
    }).catch(() => {
      setIsSearching(false);
    });
  }, [searchText, selectedNewProducts, isSearching, addExtraAttributes]);

  const handleSelectProduct = useCallback((e, product) => {
    const { checked } = e.target;

    let newSelectedProducts = [...selectedNewProducts];

    if (isEmpty(product.product_variant_combinations)) {
      if (checked) {
        if (!find(newSelectedProducts, { web_id: product.web_id })) {
          const newOrderItem = {
            ...product,
            product_id: product.id,
            brand: product.brand_name || product.brand || product.vendor || '',
            brand_name: product.brand_name || product.brand || product.vendor || '',
            custom_tax: product.custom_tax,
            description: product.description || '',
            url: product.original_url || product.url || ''
          };
          delete newOrderItem.id;

          newSelectedProducts.push(newOrderItem);
        }
      } else {
        newSelectedProducts = filter(newSelectedProducts, (p) => p.web_id !== product.web_id);
      }

      setSelectedNewProducts(newSelectedProducts);
      setProducts(products.map((prod) => {
        if (prod.id === product.id) {
          return { ...prod, selected: checked };
        }

        return prod;
      }));

      return;
    }

    product.selected = checked;

    if (checked) {
      if (isEmpty(product.product_variant_combinations) && !find(newSelectedProducts, { web_id: product.web_id })) {
        const newOrderItem = {
          ...product,
          product_id: product.id,
          brand: product.brand_name || product.brand || product.vendor || '',
          brand_name: product.brand_name || product.brand || product.vendor || '',
          custom_tax: product.custom_tax,
          description: product.description || '',
          url: product.original_url || product.url || ''
        };
        delete newOrderItem.id;

        newSelectedProducts.push(newOrderItem);
      } else {
        forEach(product.product_variant_combinations, (combination) => {
          if (canBeSelected(combination) && !find(newSelectedProducts, combination)) {
            const newOrderItem = {
              ...combination,
              title: product.title,
              product_variant_combination_id: combination.id,
              variant_name: combination.name,
              type_tax: product.type_tax,
              custom_tax: product.custom_tax,
              brand: product.brand_name || product.brand || product.vendor || '',
              brand_name: product.brand_name || product.brand || product.vendor || '',
              description: product.description || '',
              url: combination.variant_url || product.original_url || product.url || ''
            };
            delete newOrderItem.id;

            newSelectedProducts.push(newOrderItem);
          }
        });
      }
    } else if (isEmpty(product.product_variant_combinations)) {
      newSelectedProducts = filter(newSelectedProducts, (p) => p.web_id !== product.web_id);
    } else {
      forEach(product.product_variant_combinations, (combination) => {
        newSelectedProducts = filter(newSelectedProducts, (p) => p.web_id !== combination.web_id);
      });
    }

    const productIndex = products.lastIndexOf(product);
    if (productIndex !== -1) {
      forEach(products[productIndex].product_variant_combinations, (combination) => {
        combination.selected = checked;
      });
    }

    setProducts([...products]);
    setSelectedNewProducts(newSelectedProducts);
  }, [products, selectedNewProducts]);

  const handleSelectCombination = useCallback((e, product, combination) => {
    const { checked } = e.target;

    setProducts(products.map((prod) => {
      const newProduct = { ...prod };
      if (newProduct.id === product.id) {
        newProduct.selected = checked;
      }

      forEach(newProduct.product_variant_combinations, (comb) => {
        if (comb.id === combination.id) {
          comb.selected = checked;
        }
      });

      return newProduct;
    }));

    if (checked && !find(selectedProducts, { product_variant_combination_id: combination.id })) {
      const newOrderItem = {
        ...combination,
        title: product.title,
        product_variant_combination_id: combination.id,
        variant_name: combination.name,
        type_tax: product.type_tax,
        custom_tax: product.custom_tax,
        brand: product.brand_name || product.brand || product.vendor || '',
        brand_name: product.brand_name || product.brand || product.vendor || '',
        description: product.description || '',
        url: combination.variant_url || product.original_url || product.url || ''
      };
      delete newOrderItem.id;

      setSelectedNewProducts([...selectedNewProducts, newOrderItem]);
    } else {
      setSelectedNewProducts(filter(selectedNewProducts, (p) => p.product_variant_combination_id !== combination.id));
    }
  }, [products, selectedProducts, selectedNewProducts]);

  const handleAddProducts = useCallback(() => {
    addProducts(selectedNewProducts.map((product) => {
      const effectivePrice = hasDiscount(product) ? Number(product.discounted_price) : Number(product.price);
      const newOrderItem = {
        ...product,
        lineIdentifier: uuid(),
        sku: product.sku || '',
        quantity: 1,
        original_price: product.price,
        unit_price: effectivePrice || 0,
        custom_tax: getProductTaxAmount(product, tax_amount),
        wholesale_price: product.wholesale_price || 0,
        total: Number(Big(1).times(effectivePrice || 0).round(2)),
        selected: false,
        sold_wholesale: false,
        editableCustomTax: false,
        selectedProduct: null,
        discount_type: null,
        discount_value: null,
        discount_reason: '',
        total_discount: 0
      };

      delete newOrderItem.id;

      return newOrderItem;
    }));
  }, [selectedNewProducts, addProducts, tax_amount]);

  const addFakeProduct = useCallback(() => {
    const fakeProduct = {
      title: searchText,
      price: 0,
      product_id: null,
      product_variant_combination_id: null,
      product_variant_combinations: [],
      selected: true,
      wasSelected: false
    };

    setSearchedProductExists(true);
    setProducts([...products, fakeProduct]);
    setSelectedNewProducts([...selectedNewProducts, fakeProduct]);
  }, [searchText, products, selectedNewProducts]);

  return {
    products,
    pagination,
    currency_symbol,
    currency_code,
    searchText,
    searchedProductExists,
    isSearching,
    setSearchText,
    searchProducts,
    handleSelectProduct,
    handleSelectCombination,
    addFakeProduct,
    handleAddProducts
  };
};
